import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { DistributionBar } from '@/components/charts/DistributionBar';
import { Scorecard, ScorecardLegend } from '@/components/scorecard/Scorecard';
import { GroupStandings, MatchScoreboard } from '@/components/social/GroupStandings';
import { Button, ButtonLink } from '@/components/ui/Button';
import { CourseTexture } from '@/components/ui/CourseTexture';
import { Dialog } from '@/components/ui/Dialog';
import { EmptyState, Skeleton } from '@/components/ui/Feedback';
import { StatusPill } from '@/components/ui/Pill';
import { SectionBar } from '@/components/ui/SectionBar';
import { StatBlock, StatRow } from '@/components/ui/StatBlock';
import { useToast } from '@/components/ui/Toast';
import { roundsApi, usersApi } from '@/lib/api/endpoints';
import {
  qk,
  useCourse,
  useCourseIndex,
  useGroupRoundByRound,
  useRound,
} from '@/lib/api/queries';
import { useCurrentUser } from '@/lib/auth/AuthContext';
import type { Round } from '@/lib/api/types';
import { fmtDateLong } from '@/lib/format/date';
import { fmtDecimal, fmtInt, fmtOutOf, fmtPct, fmtToPar } from '@/lib/format/number';
import { t } from '@/lib/i18n';
import { useIsDesktop } from '@/lib/useMediaQuery';
import { routes } from '@/routes/paths';
import { ShareRound } from './ShareRound';
import styles from './RoundDetail.module.css';
import { ChevronLeftIcon } from '@/components/icons';
import { CheckIcon } from '@/components/icons';

export function RoundDetail() {
  const { roundId } = useParams<{ roundId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const user = useCurrentUser();
  const round = useRound(roundId);
  // `null` quando a volta é individual, que é a maioria — não é erro, é a resposta.
  const group = useGroupRoundByRound(roundId);
  const courseIndex = useCourseIndex();
  // Full detail gives the per-hole par and stroke index the scorecard needs.
  const course = useCourse(round.data?.courseId);

  const [confirm, setConfirm] = useState<'finish' | 'close9' | 'abandon' | null>(null);
  const [working, setWorking] = useState(false);
  const isDesktop = useIsDesktop();
  const [sharing, setSharing] = useState(false);

  const data = round.data;
  const summary = data ? courseIndex.data?.get(data.courseId) : undefined;

  const holes = useMemo(() => {
    if (!data) return [];
    const byNumber = new Map(data.scores.map((s) => [s.holeNumber, s]));
    const physicalCount = course.data?.totalHoles ?? data.holesPlayed;

    return Array.from({ length: data.holesPlayed }, (_, i) => {
      const number = i + 1;
      // A 9-hole course played as 18 repeats its physical holes.
      const physical = ((number - 1) % physicalCount) + 1;
      const courseHole = course.data?.holes.find((h) => h.number === physical);
      const score = byNumber.get(number);
      return {
        number,
        par: score?.par ?? courseHole?.par ?? 4,
        strokeIndex: courseHole?.handicapIndex,
        score,
      };
    });
  }, [data, course.data]);

  if (round.isLoading) return <DetailSkeleton />;

  if (round.error || !data) {
    return (
      <EmptyState
        title={t('round.notFound.title')}
        actions={
          <ButtonLink to={routes.rounds} variant="secondary" block>
            {t('round.notFound.action')}
          </ButtonLink>
        }
      >
        {t('round.notFound.body')}
      </EmptyState>
    );
  }

  const isDone = data.status === 'completed';
  const scored = data.stats.holesScored;
  const missing = data.holesPlayed - scored;

  const finish = async (asHolesPlayed?: 9 | 18) => {
    setWorking(true);
    try {
      const before = data.playerHandicapAtStart;
      await roundsApi.complete(data.id, asHolesPlayed);
      await queryClient.invalidateQueries({ queryKey: qk.round(data.id) });
      await queryClient.invalidateQueries({ queryKey: qk.allRounds });
      // Completing a round recomputes the index server-side, so read it back rather than
      // invalidating: the toast is the one place the user sees the movement they just caused.
      const after = await queryClient.fetchQuery({
        queryKey: qk.handicap,
        queryFn: usersApi.handicap,
      });
      toast.success(
        before != null && after.handicapIndex != null
          ? t('round.finishedWithHandicap', {
              before: fmtDecimal(before),
              after: fmtDecimal(after.handicapIndex),
            })
          : t('round.finishedRecalculated'),
      );
      setConfirm(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('round.finishFailed'));
    } finally {
      setWorking(false);
    }
  };

  const abandon = async () => {
    setWorking(true);
    try {
      await roundsApi.abandon(data.id);
      await queryClient.invalidateQueries({ queryKey: qk.allRounds });
      toast.success(t('round.abandonedToast'));
      navigate(routes.rounds, { replace: true });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('round.abandonFailed'));
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className={styles.page}>
      <CourseTexture location={summary?.location} veil="header" className={styles.hero}>
        <div className={styles.heroNav}>
          <Link to={routes.rounds} className={styles.back} aria-label={t('rounds.back')}>
            <ChevronLeftIcon size={22} />
          </Link>
          <span className={styles.heroLabel}>{t('round.heroLabel')}</span>
          <span style={{ width: 'var(--tap)' }} />
        </div>

        <div className={styles.heroBody}>
          <StatusPill status={data.status} />
          <h1 className={styles.course}>
            {summary?.name ?? course.data?.name ?? t('round.courseFallback')}
          </h1>
          <p className={styles.meta}>
            {t('round.meta', {
              date: fmtDateLong(data.completedAt ?? data.startedAt),
              holes: data.holesPlayed,
              // Num tee combinado o rótulo é "azul / branco" — a barra é notação, não texto.
              tee: data.teeBoxNameBack
                ? `${data.teeBoxName} / ${data.teeBoxNameBack}`
                : data.teeBoxName,
            })}
          </p>

          <div className={styles.scoreLine}>
            <span className={styles.score}>{fmtInt(data.totalStrokes)}</span>
            {data.totalToPar != null && (
              <span className={styles.toPar}>{fmtToPar(data.totalToPar)}</span>
            )}
            <span className={styles.spacer} />
            {data.scoreDifferential != null && (
              <div className={styles.differential}>
                <div className={styles.differentialLabel}>{t('round.differential')}</div>
                <div className={styles.differentialValue}>{fmtDecimal(data.scoreDifferential)}</div>
              </div>
            )}
          </div>
        </div>
      </CourseTexture>

      {!isDone && (
        <section className={styles.pendingBox}>
          <StatRow>
            <StatBlock
              label={t('round.filled')}
              value={`${scored}`}
              caption={t('round.filledOf', { total: data.holesPlayed })}
            />
            <StatBlock label={t('round.strokesSoFar')} value={fmtInt(data.stats.totalStrokes)} />
            <StatBlock label={t('round.toPar')} value={fmtToPar(data.stats.totalToPar)} />
          </StatRow>
          <div className={styles.pendingActions}>
            {missing === 0 ? (
              <Button size="lg" block onClick={() => setConfirm('finish')}>
                {t('round.finish')}
              </Button>
            ) : (
              <>
                <ButtonLink to={routes.record} size="lg">
                  {t('round.keepFilling')}
                </ButtonLink>
                {/* The server accepts `asHolesPlayed: 9` only when there are *exactly* 9
                    scores, so this is offered at 9 and nowhere else. */}
                {scored === 9 && data.holesPlayed > 9 && (
                  <Button variant="secondary" size="lg" onClick={() => setConfirm('close9')}>
                    {t('round.closeAs9')}
                  </Button>
                )}
              </>
            )}
          </div>
          <p className={styles.pendingNote}>
            {missing === 0
              ? t('round.note.complete')
              : scored === 9 && data.holesPlayed > 9
                ? t('round.note.canClose9', { count: missing, holes: data.holesPlayed })
                : t('round.note.pending', {
                    count: missing,
                    holes: data.holesPlayed,
                    scored,
                  })}
          </p>
        </section>
      )}

      <StatRow scroll className={styles.statRow}>
        <StatBlock
          label={t('round.putts')}
          value={fmtInt(data.stats.totalPutts)}
          caption={
            data.stats.averagePuttsPerHole != null
              ? t('round.puttsPerHole', { value: fmtDecimal(data.stats.averagePuttsPerHole) })
              : undefined
          }
        />
        <StatBlock
          label={t('round.gir')}
          value={fmtPct(data.stats.greensInRegulationPct)}
          pct={data.stats.greensInRegulationPct}
          caption={fmtOutOf(data.stats.greensInRegulation, scored)}
        />
        <StatBlock
          label={t('round.fairway')}
          value={fmtPct(data.stats.fairwayHitPct)}
          pct={data.stats.fairwayHitPct}
          caption={t('round.fairwayCaption', {
            value: fmtOutOf(data.stats.fairwaysHit, fairwayEligible(data)),
          })}
        />
        {/*
          Bunker e Penalty não moram aqui: eram dois números empacotados num valor só, e o leitor
          tinha que descobrir qual era qual pela posição. Os dois seguem logo abaixo, no painel
          "Estatísticas", cada um com o próprio rótulo.
        */}
        <StatBlock
          label={t('round.handicapAtStart')}
          value={fmtDecimal(data.playerHandicapAtStart)}
        />
      </StatRow>

      {/*
        O quadro do flight vem **antes** do seu scorecard: numa volta em grupo, o resultado da
        disputa é o que se abre para ver. O seu cartão continua logo abaixo, inteiro.
      */}
      {group.data && (
        <>
          <SectionBar variant="solid">{t('round.group.title')}</SectionBar>
          {/*
            No desktop o quadro fica à esquerda e a sua volta à direita (canvas 1z): as duas
            leituras são simultâneas — "quem ganhou" e "quanto isso mexeu no meu handicap".
            No celular são duas seções que o jogador rola entre, que é o que este mesmo markup
            faz quando o grid não existe.
          */}
          <div className={styles.groupLayout}>
            <div className={styles.groupMain}>
              <GroupStandings group={group.data} viewerId={user.id} />
            </div>

            {isDesktop && (
              <aside className={styles.groupAside}>
                <section className={styles.asidePanel}>
                  <h2 className={styles.asideTitle}>{t('round.group.yours')}</h2>
                  <AsideStat
                    label={t('round.strokes')}
                    value={fmtInt(data.totalStrokes)}
                    note={
                      data.totalToPar != null
                        ? t('round.toParNote', { value: fmtToPar(data.totalToPar) })
                        : null
                    }
                  />
                  <AsideStat
                    label={t('round.putts')}
                    value={fmtInt(data.stats.totalPutts)}
                    note={
                      data.stats.averagePuttsPerHole != null
                        ? t('round.puttsPerHoleShort', {
                            value: fmtDecimal(data.stats.averagePuttsPerHole),
                          })
                        : null
                    }
                  />
                  <AsideStat
                    label={t('round.gir')}
                    value={fmtPct(data.stats.greensInRegulationPct)}
                    note={fmtOutOf(data.stats.greensInRegulation, scored)}
                  />

                  {/*
                    "Só a sua volta entra no handicap; no scramble nenhuma entra." A frase muda
                    de sinal com `countsForHandicap` porque no scramble não há differential a
                    esconder — ele não existe, já que não houve bola individual.
                  */}
                  {/*
                    A frase só faz sentido depois que a volta fecha: "entrou no handicap" numa
                    volta em andamento anunciaria um efeito que ainda não houve.
                  */}
                  {!isDone ? null : data.countsForHandicap ? (
                    <div className={styles.asideHandicap}>
                      <span className={styles.asideCheck} aria-hidden="true">
                        <CheckIcon size={16} />
                      </span>
                      <span>
                        <span className={styles.asideHandicapLabel}>
                          {t('round.group.counted')}
                        </span>
                        {data.handicapIndexAfter != null && (
                          <span className={styles.asideHandicapValue}>
                            {fmtDecimal(data.playerHandicapAtStart)} →{' '}
                            {fmtDecimal(data.handicapIndexAfter)}
                          </span>
                        )}
                      </span>
                    </div>
                  ) : (
                    <p className={styles.asideNote}>{t('round.group.scrambleNote')}</p>
                  )}
                </section>

                {/*
                  O placar de match play como bloco lateral: mesmo numa volta de stroke play, é
                  o formato em que a leitura buraco a buraco cabe inteira sem rolagem. Quando a
                  volta *é* match play, ele já está no quadro à esquerda — repeti-lo aqui seria
                  o mesmo placar duas vezes.
                */}
                {group.data.match && group.data.format.scoring !== 'match' && (
                  <section className={styles.asidePanel}>
                    <h2 className={styles.asideTitle}>{t('round.group.matchWhatIf')}</h2>
                    <MatchScoreboard
                      group={group.data}
                      match={group.data.match}
                      viewerId={user.id}
                      compact
                    />
                  </section>
                )}
              </aside>
            )}
          </div>
        </>
      )}

      <SectionBar variant="solid" aside={t('round.scorecard.swipe')}>
        {/*
          No scramble a bola é da dupla, então este cartão não é seu — é do time. Chamá-lo de
          "meu scorecard" atribuiria a uma pessoa tacadas que quatro mãos deram.
        */}
        {group.data?.format.teamFormat === 'scramble'
          ? t('round.scorecard.team')
          : group.data
            ? t('round.scorecard.mine')
            : t('round.scorecard.plain')}
      </SectionBar>
      <Scorecard
        holes={holes}
        showStrokeIndex
        linkTo={
          course.data
            ? (n) => routes.hole(data.courseId, ((n - 1) % course.data.totalHoles) + 1)
            : undefined
        }
      />
      <ScorecardLegend />

      <SectionBar variant="solid">{t('round.stats')}</SectionBar>
      <section className={styles.panel}>
        <div className={styles.panelLabel}>{t('round.distribution')}</div>
        <DistributionBar distribution={data.stats} />

        <div className={styles.numbersGrid}>
          <StatBlock label={t('round.strokes')} value={fmtInt(data.stats.totalStrokes)} />
          <StatBlock label={t('round.toPar')} value={fmtToPar(data.stats.totalToPar)} />
          <StatBlock label={t('round.holes')} value={`${scored}`} />
          <StatBlock label={t('round.bunker')} value={fmtInt(data.stats.bunkerShots)} />
          <StatBlock label={t('round.penalty')} value={fmtInt(data.stats.penaltyStrokes)} />
          <StatBlock
            label={t('round.puttsPerHoleLabel')}
            value={fmtDecimal(data.stats.averagePuttsPerHole)}
          />
        </div>

        <p className={styles.note}>{t('round.statsNote', { holes: scored })}</p>
      </section>

      <SectionBar>{t('round.facts')}</SectionBar>
      <Fact label={t('round.scoreDifferential')} value={fmtDecimal(data.scoreDifferential)} />
      <Fact label={t('round.handicapAtStart')} value={fmtDecimal(data.playerHandicapAtStart)} />
      <Fact label={t('round.courseRating')} value={fmtDecimal(data.courseSnapshot.courseRating)} />
      <Fact label={t('round.slope')} value={fmtInt(data.courseSnapshot.slopeRating)} />
      <Fact label={t('round.coursePar')} value={fmtInt(data.courseSnapshot.par)} />
      <p className={styles.factNote}>{t('round.factNote')}</p>

      <div className={styles.actions}>
        <Button size="lg" onClick={() => setSharing(true)} disabled={!isDone}>
          {t('round.share')}
        </Button>
        <ButtonLink to={routes.course(data.courseId)} variant="secondary" size="lg">
          {t('round.viewCourse')}
        </ButtonLink>
      </div>

      <Dialog
        open={confirm === 'finish'}
        onClose={() => setConfirm(null)}
        title={t('round.finishDialog.title')}
        confirmLabel={t('rounds.finishAction')}
        onConfirm={() => void finish()}
        confirmBusy={working}
        cancelLabel={t('round.finishDialog.cancel')}
        stats={
          <>
            <StatBlock label={t('round.score')} value={fmtInt(data.stats.totalStrokes)} />
            <StatBlock label={t('round.toPar')} value={fmtToPar(data.stats.totalToPar)} />
            <StatBlock label={t('round.holes')} value={`${scored}`} />
          </>
        }
      >
        {t('round.finishDialog.body')}
      </Dialog>

      <Dialog
        open={confirm === 'close9'}
        onClose={() => setConfirm(null)}
        title={t('round.close9.title')}
        confirmLabel={t('round.close9.confirm')}
        onConfirm={() => void finish(9)}
        confirmBusy={working}
        cancelLabel={t('round.close9.cancel')}
      >
        {t('round.close9.body', { count: scored })}
      </Dialog>

      <Dialog
        open={confirm === 'abandon'}
        onClose={() => setConfirm(null)}
        title={t('round.abandonDialog.title')}
        confirmLabel={t('round.abandonDialog.confirm')}
        onConfirm={() => setConfirm(null)}
        cancelLabel={t('rounds.back')}
        destructiveLabel={t('rounds.abandonAction')}
        onDestructive={() => void abandon()}
      >
        {t('round.abandonDialog.body', { count: scored })}
      </Dialog>

      <ShareRound round={data} course={summary} open={sharing} onClose={() => setSharing(false)} />

      {!isDone && (
        <div className={styles.dangerZone}>
          <Button variant="danger" block onClick={() => setConfirm('abandon')}>
            {t('round.abandon')}
          </Button>
        </div>
      )}
    </div>
  );
}

/** Holes that could have had a fairway hit — par 3s are excluded by the server. */
function fairwayEligible(round: Round): number {
  return round.scores.filter((s) => s.par !== 3).length;
}

/** Uma linha de "Sua volta": rótulo à esquerda, número e sua qualificação à direita. */
function AsideStat({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string | null;
}) {
  return (
    <div className={styles.asideStat}>
      <span className={styles.asideStatLabel}>{label}</span>
      <span className={styles.asideStatValue}>{value}</span>
      {note && <span className={styles.asideStatNote}>{note}</span>}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.factRow}>
      <span className={styles.factLabel}>{label}</span>
      <span className={styles.factValue}>{value}</span>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className={styles.page}>
      <div style={{ background: 'var(--green)', padding: '2rem var(--gutter) 2.5rem' }}>
        <Skeleton width="60%" height={26} style={{ opacity: 0.25 }} />
        <Skeleton width="40%" height={12} style={{ marginTop: 12, opacity: 0.25 }} />
        <Skeleton width="30%" height={56} style={{ marginTop: 20, opacity: 0.25 }} />
      </div>
      <div style={{ padding: 'var(--space-5) var(--gutter)', display: 'flex', gap: 16 }}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{ flex: 1 }}>
            <Skeleton height={9} />
            <Skeleton height={26} style={{ marginTop: 8 }} />
          </div>
        ))}
      </div>
    </div>
  );
}
